import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import {
  type ChangeEvent,
  type FocusEvent,
  type ReactNode,
  type RefObject,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@/components/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOnline } from "@/hooks/use-online"
import { adminInvitationsPath } from "@/routes"

interface InviteAdminDialogProps {
  /**
   * Trigger element passed via `<DialogTrigger asChild>`. Typically the
   * `Invite admin` button on the Admin Overview header. Required because
   * mounting via state-controlled `open` (without a `<DialogTrigger>` ancestor)
   * loses the auto focus-return behaviour shadcn provides — the trigger
   * needs to be in scope so close-handler can return focus to it.
   */
  trigger: ReactNode
  /**
   * Optional ref forwarded to the underlying trigger so callers (e.g.
   * `PendingFirstAdminInviteCard`'s orphan EmptyState CTA) can hand back
   * focus deliberately on close. Standard shadcn flow already covers the
   * trigger-on-page case.
   */
  triggerRef?: RefObject<HTMLElement | null>
}

/**
 * OKS-12 Path A — Invite OKS Admin (Admin Overview header).
 *
 * Single-field dialog: just `email`. Posts to a single backend endpoint
 * (`POST /admin/invitations` with `path: "admin"` discriminator per
 * Backend TRD). The Inertia render-prop `errors` carries field-level
 * validation; non-field errors land in `errors.base` and surface in a
 * top banner.
 *
 * Crew form rules: Inertia `<Form>` (NOT shadcn `Form`); plain `Input` +
 * `Label` + `<InputError>`; no react-hook-form; no zod. Email is auto
 * `trim()` + `toLowerCase()` on blur per UX Spec §Micro-Interactions —
 * keeps the same value the server canonicalises so what the user sees
 * matches what gets stored.
 *
 * **Non-dismissable while submitting.** `<Dialog onOpenChange>` blocks
 * ESC + overlay-click while `processing`, matching UX Spec §States. ESC
 * still works once the request settles. Tab order: Email → Cancel →
 * Submit (Path A skips the Locale field that B/C have).
 *
 * Locked QA testid:
 *   - `admin-invite-admin-trigger` on the trigger element
 *   - `admin-invite-admin-dialog` on the DialogContent
 *   - `admin-invite-admin-submit` on the primary button
 */
export function InviteAdminDialog({
  trigger,
  triggerRef,
}: InviteAdminDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  /**
   * Mid-submit guard for `onOpenChange`. The `<Dialog>` `onOpenChange`
   * fires outside the Inertia `<Form>` render-prop scope so we can't
   * read `processing` directly from there. The ref is mirrored from
   * inside the render-prop on every render — by the time
   * `onOpenChange(false)` fires (ESC / overlay click), the latest
   * `processing` value is already in `submittingRef.current`.
   */
  const submittingRef = useRef(false)

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value)
  }

  function handleEmailBlur(event: FocusEvent<HTMLInputElement>) {
    // UX Spec §Micro-Interactions — auto-canonicalise to match server.
    const next = event.target.value.trim().toLowerCase()
    if (next !== email) setEmail(next)
  }

  function handleClose() {
    setOpen(false)
    setEmail("")
    // State-controlled `open` doesn't ride the `<DialogTrigger>`
    // focus-return contract by default — restore manually so keyboard
    // users land back on the invoking trigger after close.
    triggerRef?.current?.focus()
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        // Block ESC + overlay-click while the request is in flight.
        // `next === false` means the user is trying to close; we ignore
        // it during submit. The render-prop `processing` flag is owned
        // by Inertia `<Form>` below — we mirror it via local state on
        // submit start because `onOpenChange` fires outside the Form's
        // render context.
        if (!next && submittingRef.current) return
        if (!next) handleClose()
        else setOpen(true)
      }}
    >
      {/*
       * `<ResponsiveDialogTrigger asChild>` uses Radix `Slot` which merges
       * its props onto the child Button — `data-testid` from this wrapper
       * is overridden by any `data-testid` on the child. Don't set one
       * here; the parent page is responsible for putting a stable
       * testid on its own Button (locked contract:
       * `admin-invite-admin-button`).
       */}
      <ResponsiveDialogTrigger asChild>{trigger}</ResponsiveDialogTrigger>
      <ResponsiveDialogContent
        className="sm:max-w-md"
        data-testid="admin-invite-admin-dialog"
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>
            {t("admin.invites.dialog.invite_admin.title")}
          </ResponsiveDialogTitle>
          {/*
           * sr-only Description distinct from the Title — Radix
           * requires a description for a11y compliance, but using the
           * title key would make SR users hear "Invite admin. Invite
           * admin." Use a meaningful "what this dialog does" string
           * instead.
           */}
          <ResponsiveDialogDescription className="sr-only">
            {t("admin.invites.dialog.invite_admin.description")}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {/*
         * OKS-120 — Form className shim: in mobile Sheet mode, the
         * Form acts as the scrollable body between the sticky header
         * and sticky footer. `flex flex-1 min-h-0 overflow-y-auto p-4`
         * is neutral inside the desktop DialogContent grid container
         * (no flex parent → flex-1 collapses; no overflowing parent →
         * overflow-y-auto is inert). At <640px the parent SheetContent
         * is a flex column with `h-svh`, so the Form takes the
         * remaining height and scrolls internally.
         */}
        <Form
          action={adminInvitationsPath()}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={handleClose}
          resetOnSuccess
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-0"
        >
          {({ processing, errors }) => {
            // Mirror `processing` into the ref consulted by
            // `onOpenChange` above. Cheap; runs on every render-prop
            // tick which already happens on processing transitions.
            submittingRef.current = processing
            return (
              <>
                {/*
                 * Path discriminator — backend's single controller routes on
                 * this body field per Backend TRD §Approach. Hidden input
                 * keeps the value alongside the email + away from FE-side
                 * inspection, which is what we want for a server-driven
                 * contract.
                 */}
                <input type="hidden" name="path" value="admin" />

                {/* Top-of-dialog banner for non-field errors (`errors.base`).
                 * Inertia errors arrive as either `string` or `string[]`
                 * depending on adapter version; we coerce to a joined
                 * string so the banner renders consistently regardless. */}
                {errors.base && (
                  <div
                    role="alert"
                    className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="admin-invite-admin-error-banner"
                  >
                    {Array.isArray(errors.base)
                      ? errors.base.join(" ")
                      : errors.base}
                  </div>
                )}
                {/* Offline banner — UX Spec §States */}
                {!online && (
                  <div
                    className="border-muted-foreground/20 bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="admin-invite-admin-offline-banner"
                  >
                    {t("admin.invites.dialog.offline_banner")}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Label htmlFor="invite-admin-email">
                    {t("admin.invites.dialog.invite_admin.email_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="invite-admin-email"
                    name="email"
                    type="email"
                    autoComplete="off"
                    // UX Spec §Accessibility: focus on Email when dialog opens.
                    // Non-destructive surface — primary input gets autoFocus
                    // (Crew dialog convention; Cancel-autoFocus is reserved for
                    // destructive AlertDialogs).
                    autoFocus
                    aria-required="true"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    placeholder={t(
                      "admin.invites.dialog.invite_admin.email_placeholder",
                    )}
                    aria-invalid={errors.email ? "true" : undefined}
                    aria-describedby={
                      errors.email ? "invite-admin-email-error" : undefined
                    }
                    disabled={!online || processing}
                  />
                  <InputError
                    messages={errors.email}
                    id="invite-admin-email-error"
                  />
                </div>

                <ResponsiveDialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={processing}
                    aria-disabled={processing ? "true" : undefined}
                  >
                    {t("admin.invites.dialog.invite_admin.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={processing || !online}
                    aria-disabled={processing || !online ? "true" : undefined}
                    data-testid="admin-invite-admin-submit"
                  >
                    {processing && (
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    {processing
                      ? t("admin.invites.dialog.invite_admin.sending")
                      : t("admin.invites.dialog.invite_admin.submit")}
                  </Button>
                </ResponsiveDialogFooter>
              </>
            )
          }}
        </Form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
